import React, { useState, useEffect, useMemo } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAdminUnreadMessages } from "../hooks/useAdminUnreadMessages";
import { useStore } from "../context/StoreContext";
import { useAuth } from "../context/AuthContext";
import {
  FaChartBar,
  FaBox,
  FaTags,
  FaCreditCard,
  FaTruck,
  FaExchangeAlt,
  FaFileAlt,
  FaComments,
  FaBars,
  FaTimes,
  FaChevronDown,
  FaChevronRight,
  FaWarehouse,
  FaMagic,
  FaImages,
  FaWallet,
  FaMoneyCheckAlt,
  FaCog,
  FaLanguage,
  FaPaintBrush,
  FaLayerGroup,
  FaShoppingBag,
  FaReceipt,
  FaCoins,
  FaPercent,
  FaHandHoldingUsd,
  FaShippingFast,
  FaMapMarkerAlt,
  FaShieldAlt,
  FaUser,
  FaUserPlus,
  FaPlus,
} from "react-icons/fa";

interface AdminSidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  storeLogo?: string;
  storeName?: string;
}

interface MenuItem {
  key?: string;
  path?: string;
  label: string;
  icon: React.ReactNode;
  children?: MenuItem[];
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({
  isCollapsed,
  onToggle,
  storeLogo,
  storeName,
}) => {
  const { t } = useTranslation();
  const { isPluginEnabled } = useStore();
  const { user } = useAuth();
  const [isMobile, setIsMobile] = useState(false);
  const [openMenus, setOpenMenus] = useState<Set<string>>(new Set());
  const location = useLocation();
  const unreadCount = useAdminUnreadMessages();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Auto-open menu if current path matches
  const currentPath = location.pathname;
  useEffect(() => {
    const pathToMenuKey: Record<string, string> = {
      "/admin/products": "catalog",
      "/admin/categories": "catalog",
      "/admin/inventory": "catalog",
      "/admin/gallery": "catalog",
      "/admin/orders": "sales",
      "/admin/carts": "sales",
      "/admin/payments": "sales",
      "/admin/users": "sales",
      "/admin/invitations": "sales",
      "/admin/payout": "financial",
      "/admin/profit-rules": "financial",
      "/admin/contributor-payouts": "financial",
      "/admin/payment-methods": "checkout",
      "/admin/shipping": "operations",
      "/admin/locations": "operations",
      "/admin/currencies": "operations",
      "/admin/subscription": "settings",
      "/admin/theme-settings": "settings",
      "/admin/language-settings": "settings",
      "/admin/address-field-settings": "settings",
      "/admin/store-gdpr": "settings",
      "/admin/pages": "content",
    };

    const menuKey = Object.keys(pathToMenuKey).find((path) =>
      currentPath.startsWith(path),
    );
    if (menuKey) {
      setOpenMenus(new Set([pathToMenuKey[menuKey]]));
    }
  }, [currentPath]);

  const toggleMenu = (menuKey: string) => {
    setOpenMenus((prev) => {
      const next = new Set(prev);
      next.has(menuKey) ? next.delete(menuKey) : next.add(menuKey);
      return next;
    });
  };

  // Define menu structure
  const menuItems: MenuItem[] = useMemo(() => {
    const isProfitPluginEnabled = isPluginEnabled("profit-rules");

    const catalogChildren = [
      {
        path: "/admin/products",
        label: t("admin.sidebar.products"),
        icon: <FaBox className="text-sm" />,
      },
      {
        path: "/admin/categories",
        label: t("admin.sidebar.categories"),
        icon: <FaTags className="text-sm" />,
      },
      {
        path: "/admin/inventory",
        label: t("admin.sidebar.inventory"),
        icon: <FaWarehouse className="text-sm" />,
      },
    ];

    // Always add Gallery (replaces customization assets, but customization assets are still available as a tab)
    catalogChildren.push({
      path: "/admin/gallery",
      label: t("admin.sidebar.gallery"),
      icon: <FaImages className="text-sm" />,
    });

    const baseMenuItems: MenuItem[] = [
      {
        key: "dashboard",
        path: "/admin/dashboard",
        label: t("admin.sidebar.dashboard"),
        icon: <FaChartBar className="text-lg" />,
      },
      {
        key: "catalog",
        label: t("admin.sidebar.catalog"),
        icon: <FaLayerGroup className="text-lg" />,
        children: catalogChildren,
      },
      {
        key: "sales",
        label: t("admin.sidebar.salesOrders"),
        icon: <FaReceipt className="text-lg" />,
        children: [
          {
            path: "/admin/orders/create",
            label: t("admin.sidebar.createOrder"),
            icon: <FaPlus className="text-sm" />,
          },
          {
            path: "/admin/orders",
            label: t("admin.sidebar.orders"),
            icon: <FaShoppingBag className="text-sm" />,
          },
          {
            path: "/admin/carts",
            label: t("admin.sidebar.carts"),
            icon: <FaShoppingBag className="text-sm" />,
          },
          {
            path: "/admin/payments",
            label: t("admin.sidebar.payments"),
            icon: <FaCreditCard className="text-sm" />,
          },
          {
            path: "/admin/users",
            label: t("admin.sidebar.users"),
            icon: <FaUser className="text-sm" />,
          },
          ...(user?.role === "store_admin"
            ? [
                {
                  path: "/admin/invitations",
                  label: t("admin.sidebar.invitations"),
                  icon: <FaUserPlus className="text-sm" />,
                },
              ]
            : []),
        ],
      },
    ];

    // Only add financial menu if profit plugin is enabled
    if (isProfitPluginEnabled) {
      baseMenuItems.push({
        key: "financial",
        label: t("admin.sidebar.financial"),
        icon: <FaCoins className="text-lg" />,
        children: [
          {
            path: "/admin/profit-rules-settings",
            label: t("admin.sidebar.profitRulesSettings"),
            icon: <FaPercent className="text-sm" />,
          },
          {
            path: "/admin/contributor-payouts",
            label: t("admin.sidebar.contributorPayouts"),
            icon: <FaHandHoldingUsd className="text-sm" />,
          },
        ],
      });
    }

    baseMenuItems.push(
      {
        key: "checkout",
        label: t("admin.sidebar.checkout"),
        icon: <FaCreditCard className="text-lg" />,
        children: [
          {
            path: "/admin/payment-methods",
            label: t("admin.sidebar.paymentMethods"),
            icon: <FaWallet className="text-sm" />,
          },
          {
            path: "/admin/payment-gateways",
            label: t("admin.sidebar.paymentGateways"),
            icon: <FaMoneyCheckAlt className="text-sm" />,
          },
        ],
      },
      {
        key: "operations",
        label: t("admin.sidebar.operations"),
        icon: <FaShippingFast className="text-lg" />,
        children: [
          {
            path: "/admin/shipping",
            label: t("admin.sidebar.shipping"),
            icon: <FaTruck className="text-sm" />,
          },
          {
            path: "/admin/locations",
            label: t("admin.sidebar.countries"),
            icon: <FaMapMarkerAlt className="text-sm" />,
          },
          {
            path: "/admin/currencies",
            label: t("admin.sidebar.currencies"),
            icon: <FaExchangeAlt className="text-sm" />,
          },
        ],
      },
      {
        key: "settings",
        label: t("admin.sidebar.settings"),
        icon: <FaCog className="text-lg" />,
        children: [
          {
            path: "/admin/subscription",
            label: t("admin.sidebar.subscription"),
            icon: <FaCreditCard className="text-sm" />,
          },
          {
            path: "/admin/theme-settings",
            label: t("admin.sidebar.themeSettings"),
            icon: <FaPaintBrush className="text-sm" />,
          },
          {
            path: "/admin/language-settings",
            label: t("admin.sidebar.languageSettings"),
            icon: <FaLanguage className="text-sm" />,
          },
          {
            path: "/admin/address-field-settings",
            label: t("admin.sidebar.addressFieldSettings") || "Address Fields",
            icon: <FaMapMarkerAlt className="text-sm" />,
          },
          {
            path: "/admin/store-gdpr",
            label: t("admin.sidebar.storeGdpr", {
              defaultValue: "GDPR & Data",
            }),
            icon: <FaShieldAlt className="text-sm" />,
          },
        ],
      },
      {
        key: "content",
        path: "/admin/pages",
        label: t("admin.sidebar.websitePages"),
        icon: <FaFileAlt className="text-lg" />,
      },
      {
        key: "support",
        path: "/admin/helpdesk",
        label: t("admin.sidebar.support"),
        icon: <FaComments className="text-lg" />,
      },
    );

    return baseMenuItems;
  }, [t, isPluginEnabled]);

  const navLinkClassName = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 ${isCollapsed && !isMobile ? "px-2 justify-center" : "px-4"} py-2.5 transition-all duration-200 ${
      isActive
        ? "bg-blue-600 text-white font-semibold"
        : "text-gray-300 hover:bg-opacity-20 hover:bg-white hover:text-white"
    }`;

  // On mobile, sidebar should be a drawer overlay
  if (isMobile) {
    return (
      <>
        {/* Mobile Overlay */}
        {!isCollapsed && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 lg:hidden"
            onClick={onToggle}
          />
        )}
        {/* Mobile Sidebar */}
        <aside
          className={`admin-sidebar fixed left-0 top-0 h-screen bg-gray-900 text-white z-50 flex flex-col transition-transform duration-300 ${
            isCollapsed ? "-translate-x-full" : "translate-x-0"
          } w-64 lg:hidden flex-shrink-0`}
          dir={document.documentElement.dir || "ltr"}
        >
          {/* Header - Fixed */}
          <div className="p-6 pb-4 border-b border-gray-700 flex-shrink-0 flex items-center justify-between">
            <div className="flex-1 flex items-center gap-3">
              {storeLogo ? (
                <Link to="/">
                  <div className="mb-3">
                    <img
                      className="h-10 w-auto"
                      src={storeLogo}
                      alt={storeName || "Store"}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = "/logo512.png";
                      }}
                    />
                  </div>
                </Link>
              ) : null}
              <div>
                <h2 className="text-xl font-bold text-blue-400">
                  {t("admin.sidebar.adminPanel")}
                </h2>
                <p className="text-xs text-gray-400 mt-1">
                  {t("admin.sidebar.managementSuite")}
                </p>
              </div>
            </div>
            <button
              onClick={onToggle}
              className="lg:hidden p-2 hover:bg-opacity-20 hover:bg-white transition-colors"
              aria-label={t("admin.sidebar.closeSidebar")}
            >
              <FaTimes className="text-blue-400 text-xl" />
            </button>
          </div>

          {/* Navigation - Scrollable */}
          <nav className="flex-1 overflow-y-auto overflow-x-hidden p-6 pt-4 flex flex-col">
            <div className="flex-1">
              {menuItems.map((item) => {
                const hasChildren = item.children && item.children.length > 0;
                const menuKey =
                  item.key ||
                  item.path?.replace("/admin/", "") ||
                  item.label.toLowerCase().replace(/\s+/g, "-");
                const isOpen = openMenus.has(menuKey);

                if (hasChildren) {
                  return (
                    <div key={menuKey} className="mb-1">
                      <button
                        onClick={() => toggleMenu(menuKey)}
                        className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 transition-all duration-200 text-gray-300 hover:bg-opacity-20 hover:bg-white hover:text-white ${
                          isOpen ? "bg-opacity-20 bg-white" : ""
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {item.icon}
                          <span className="font-medium">{item.label}</span>
                        </div>
                        {isOpen ? (
                          <FaChevronDown className="text-sm flex-shrink-0" />
                        ) : (
                          <FaChevronRight className="text-sm flex-shrink-0" />
                        )}
                      </button>
                      {isOpen && (
                        <div className="ml-4 mt-1 space-y-1 pl-2">
                          {item.children?.map((child) => {
                            const childIsActive =
                              child.path && currentPath.startsWith(child.path);
                            return (
                              <NavLink
                                key={child.path}
                                to={child.path!}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (isMobile) {
                                    onToggle();
                                  }
                                }}
                                className={({ isActive: navIsActive }) =>
                                  `flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                                    navIsActive || childIsActive
                                      ? "bg-blue-600 text-white font-semibold"
                                      : "text-gray-300 hover:bg-opacity-20 hover:bg-white hover:text-white"
                                  }`
                                }
                              >
                                {child.icon}
                                <span>{child.label}</span>
                              </NavLink>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }

                // Check if this is the support menu item and show badge
                const isSupportItem =
                  item.key === "support" || item.path === "/admin/helpdesk";

                return (
                  <NavLink
                    key={item.path}
                    to={item.path!}
                    className={navLinkClassName}
                    onClick={() => isMobile && onToggle()}
                  >
                    <div className="relative flex items-center gap-3">
                      {item.icon}
                      <span className="font-medium">{item.label}</span>
                      {isSupportItem && unreadCount > 0 && (
                        <span className="ml-auto bg-red-500 text-white text-xs font-bold min-w-[20px] h-5 px-1.5 flex items-center justify-center">
                          {unreadCount > 99 ? "99+" : unreadCount}
                        </span>
                      )}
                    </div>
                  </NavLink>
                );
              })}
            </div>

            {/* Wizard Link at Bottom */}
            <div className="mt-auto pt-4 border-t border-gray-700">
              <NavLink
                to="/admin/setup-wizard"
                className={navLinkClassName}
                onClick={() => isMobile && onToggle()}
              >
                <div className="flex items-center gap-3">
                  <FaMagic className="text-lg" />
                  <span className="font-medium">
                    {t("admin.sidebar.setupWizard")}
                  </span>
                </div>
              </NavLink>
            </div>
          </nav>
        </aside>
      </>
    );
  }

  // Desktop Sidebar
  return (
    <aside
      className={`admin-sidebar fixed left-0 top-0 h-screen bg-gray-900 text-white flex flex-col transition-all duration-300 ${
        isCollapsed ? "w-20 lg:shadow-2xl" : "w-64"
      } hidden lg:flex z-50`}
      dir={document.documentElement.dir || "ltr"}
    >
      {/* Header - Fixed */}
      <div
        className={`p-6 pb-4 border-b border-gray-700 flex-shrink-0 ${isCollapsed ? "px-2 py-4" : ""}`}
      >
        <div
          className={`flex items-center ${isCollapsed ? "justify-center" : "justify-between"}`}
        >
          {!isCollapsed && (
            <div className="flex-1 flex items-center gap-3">
              {storeLogo ? (
                <Link to="/">
                  <div className="mb-3">
                    <img
                      className="h-10 w-auto"
                      src={storeLogo}
                      alt={storeName || "Store"}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = "/logo512.png";
                      }}
                    />
                  </div>
                </Link>
              ) : null}
              <div>
                <h2 className="text-xl font-bold text-blue-400">
                  {t("admin.sidebar.adminPanel")}
                </h2>
                <p className="text-xs text-gray-400 mt-1">
                  {t("admin.sidebar.managementSuite")}
                </p>
              </div>
            </div>
          )}
          <button
            onClick={onToggle}
            className="p-2 hover:bg-opacity-20 hover:bg-white transition-colors flex-shrink-0"
            aria-label={
              isCollapsed
                ? t("admin.sidebar.expandSidebar")
                : t("admin.sidebar.collapseSidebar")
            }
          >
            {isCollapsed ? (
              <FaBars className="text-blue-400 text-xl" />
            ) : (
              <FaTimes className="text-blue-400 text-xl" />
            )}
          </button>
        </div>
      </div>

      {/* Navigation - Scrollable */}
      <nav
        className={`flex-1 overflow-y-auto overflow-x-hidden pt-4 ${isCollapsed ? "px-2" : "p-6"} flex flex-col`}
      >
        <div className="flex-1">
          {menuItems.map((item) => {
            const hasChildren = item.children && item.children.length > 0;
            const menuKey =
              item.key ||
              item.path?.replace("/admin/", "") ||
              item.label.toLowerCase().replace(/\s+/g, "-");
            const isOpen = openMenus.has(menuKey);

            if (hasChildren) {
              // If collapsed, show parent icon and children when opened
              if (isCollapsed) {
                return (
                  <div key={menuKey} className="mb-1">
                    <button
                      onClick={() => toggleMenu(menuKey)}
                      className={`w-full flex items-center justify-center p-2.5 transition-all duration-200 text-gray-300 hover:bg-opacity-20 hover:bg-white hover:text-white ${
                        isOpen ? "bg-opacity-20 bg-white" : ""
                      }`}
                      title={item.label}
                    >
                      {item.icon}
                    </button>
                    {isOpen && (
                      <div className="mt-1 space-y-1">
                        {item.children?.map((child) => {
                          const childIsActive =
                            child.path && currentPath.startsWith(child.path);
                          return (
                            <NavLink
                              key={child.path}
                              to={child.path!}
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              className={({ isActive: navIsActive }) =>
                                `flex items-center justify-center p-2 text-sm transition-colors ${
                                  navIsActive || childIsActive
                                    ? "bg-blue-600 text-white font-semibold"
                                    : "text-gray-300 hover:bg-opacity-20 hover:bg-white hover:text-white"
                                }`
                              }
                              title={child.label}
                            >
                              {child.icon}
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <div key={menuKey} className="mb-1">
                  <button
                    onClick={() => toggleMenu(menuKey)}
                    className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 transition-all duration-200 text-gray-300 hover:bg-opacity-20 hover:bg-white hover:text-white ${
                      isOpen ? "bg-opacity-20 bg-white" : ""
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {item.icon}
                      <span className="font-medium">{item.label}</span>
                    </div>
                    {isOpen ? (
                      <FaChevronDown className="text-sm flex-shrink-0" />
                    ) : (
                      <FaChevronRight className="text-sm flex-shrink-0" />
                    )}
                  </button>
                  {isOpen && (
                    <div className="ml-4 mt-1 space-y-1 pl-2">
                      {item.children?.map((child) => {
                        const childIsActive =
                          child.path && currentPath.startsWith(child.path);
                        return (
                          <NavLink
                            key={child.path}
                            to={child.path!}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            className={({ isActive: navIsActive }) =>
                              `flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                                navIsActive || childIsActive
                                  ? "bg-blue-600 text-white font-semibold"
                                  : "text-gray-300 hover:bg-opacity-20 hover:bg-white hover:text-white"
                              }`
                            }
                            title={isCollapsed ? child.label : ""}
                          >
                            {child.icon}
                            <span>{child.label}</span>
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            // Check if this is the support menu item and show badge
            const isSupportItem =
              item.key === "support" || item.path === "/admin/helpdesk";

            return (
              <NavLink
                key={item.path}
                to={item.path!}
                className={navLinkClassName}
                title={isCollapsed ? item.label : ""}
              >
                <div className="relative flex items-center gap-3">
                  {item.icon}
                  {!isCollapsed && (
                    <span className="font-medium">{item.label}</span>
                  )}
                  {isSupportItem && unreadCount > 0 && (
                    <span
                      className={`ml-auto bg-red-500 text-white text-xs font-bold min-w-[20px] h-5 px-1.5 flex items-center justify-center ${isCollapsed ? "absolute -top-1 -right-1" : ""}`}
                    >
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </div>
              </NavLink>
            );
          })}
        </div>

        {/* Wizard Link at Bottom */}
        <div
          className={`mt-auto pt-4 border-t border-gray-700 ${isCollapsed ? "px-2" : ""}`}
        >
          <NavLink
            to="/admin/setup-wizard"
            className={navLinkClassName}
            title={isCollapsed ? t("admin.sidebar.setupWizard") : ""}
          >
            <div className={`flex items-center ${isCollapsed ? "" : "gap-3"}`}>
              <FaMagic className="text-lg" />
              {!isCollapsed && (
                <span className="font-medium">
                  {t("admin.sidebar.setupWizard")}
                </span>
              )}
            </div>
          </NavLink>
        </div>
      </nav>
    </aside>
  );
};

export default AdminSidebar;
